import type { ReactNode } from 'react';

export type StateTone = 'neutral' | 'quiet' | 'alert';

interface Props {
  icon: ReactNode;
  title: string;
  body?: string;
  hint?: string | null;
  tone?: StateTone;
  action?: ReactNode;
  /** Announce the state to a screen reader when it appears mid-flow. */
  live?: boolean;
}

// The one shape every non-content screen takes: coming-soon, empty, expired,
// offline, submitted. They are the states an attendee is most likely to hit and
// least likely to forgive, so they get the same considered treatment as the
// pages that worked — a mark, a sentence that says what happened, and a
// sentence that says what to do about it.

const TONE: Record<StateTone, { ring: string; mark: string }> = {
  neutral: { ring: 'ring-brand-hairline', mark: 'bg-brand-soft text-brand-primary' },
  quiet: { ring: 'ring-brand-hairline', mark: 'bg-brand-elevate text-brand-muted' },
  alert: { ring: 'ring-brand-outline', mark: 'bg-brand-soft text-brand-error' },
};

export function StateCard({ icon, title, body, hint, tone = 'neutral', action, live }: Props) {
  const styles = TONE[tone];
  return (
    <section
      className={`e67-card animate-fade-up px-6 py-12 text-center ring-1 ${styles.ring} sm:px-10 sm:py-16`}
      aria-live={live ? 'polite' : undefined}
    >
      <div
        className={`mx-auto flex h-14 w-14 items-center justify-center rounded-brand-lg ${styles.mark}`}
        aria-hidden="true"
      >
        {icon}
      </div>
      <h2 className="e67-title mt-6 text-balance text-2xl sm:text-[28px]">{title}</h2>
      {body && <p className="e67-prose mx-auto mt-3 max-w-md text-pretty">{body}</p>}
      {hint && <p className="mx-auto mt-4 max-w-md text-[13px] text-brand-muted">{hint}</p>}
      {action && <div className="mt-8 flex justify-center">{action}</div>}
    </section>
  );
}

const strokeProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  className: 'h-6 w-6',
};

export const Icons = {
  clock: (
    <svg {...strokeProps} aria-hidden="true" role="presentation">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  calendar: (
    <svg {...strokeProps} aria-hidden="true" role="presentation">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 10h18" />
    </svg>
  ),
  people: (
    <svg {...strokeProps} aria-hidden="true" role="presentation">
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 20a5.5 5.5 0 0 1 11 0M16 5.5a3.2 3.2 0 0 1 0 6M17.5 20a5.6 5.6 0 0 0-2.2-4.4" />
    </svg>
  ),
  lock: (
    <svg {...strokeProps} aria-hidden="true" role="presentation">
      <rect x="4.5" y="10" width="15" height="10" rx="2" />
      <path d="M8 10V7.5a4 4 0 0 1 8 0V10" />
    </svg>
  ),
  check: (
    <svg {...strokeProps} aria-hidden="true" role="presentation">
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12.3 2.4 2.4 4.6-5" />
    </svg>
  ),
  compass: (
    <svg {...strokeProps} aria-hidden="true" role="presentation">
      <circle cx="12" cy="12" r="9" />
      <path d="m14.8 9.2-1.4 4.2-4.2 1.4 1.4-4.2z" />
    </svg>
  ),
  offline: (
    <svg {...strokeProps} aria-hidden="true" role="presentation">
      <path d="M3 3l18 18M8.5 16.4a5 5 0 0 1 7 0M5 13a9.6 9.6 0 0 1 3.2-2.1M19 13a9.7 9.7 0 0 0-4.6-2.6M2 9.5A14 14 0 0 1 7 6.3M22 9.5a14 14 0 0 0-8.6-3.4" />
      <circle cx="12" cy="19.5" r="0.8" fill="currentColor" />
    </svg>
  ),
  warning: (
    <svg {...strokeProps} aria-hidden="true" role="presentation">
      <path d="M12 4.5 21 19.5H3z" />
      <path d="M12 10v4M12 17.2v.1" />
    </svg>
  ),
};
